import { useState, type CSSProperties, type FormEvent } from 'react';
import { BellRing, Check, Droplets, Flame, Plus, Settings2, Sparkles, Trash2 } from 'lucide-react';
import { api, get, post, remove } from './api';
import { Button, Card, Field, FormGrid, Modal, Progress, SectionHeader } from './components';
import { WebTimeField } from './SafeDateTimeFields';
import { showUndo, type UndoOffer } from './undo';

export type WellnessData = {
  date: string;
  habits: Array<{ id: number; name: string; frequency: string; days: number[]; color: string; reminderTime?: string | null; completed: boolean; streak: number }>;
  habitSummary: { completed: number; total: number };
  hydration: { consumedMl: number; goalMl: number; remainingMl: number; progress: number; nextReminderAt?: string | null };
  settings: { waterGoalMl: number; reminderIntervalMinutes: number; reminderStartTime: string; reminderEndTime: string; remindersEnabled: boolean };
  undo?: UndoOffer | null;
};

export function DashboardWellness({ data, onChange, onError }: {
  data: WellnessData;
  onChange: (data: WellnessData) => void;
  onError: (message: string) => void;
}) {
  const [habitOpen, setHabitOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deletingHabit, setDeletingHabit] = useState<WellnessData['habits'][number] | null>(null);
  const [busyHabit, setBusyHabit] = useState<number | null>(null);
  const [waterBusy, setWaterBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  async function checkHabit(habitId: number, completed: boolean) {
    setBusyHabit(habitId);
    try { onChange(await post<WellnessData>(`/api/wellness/habits/${habitId}/checkin`, { date: data.date, completed })); }
    catch (error) { onError((error as Error).message); }
    finally { setBusyHabit(null); }
  }

  async function addWater(amountMl: number) {
    setWaterBusy(true);
    try {
      const updated = await post<WellnessData>('/api/wellness/hydration', { date: data.date, amountMl });
      onChange(updated);
      showUndo(updated.undo, async () => onChange(await get<WellnessData>(`/api/wellness?date=${encodeURIComponent(data.date)}`)));
    }
    catch (error) { onError((error as Error).message); }
    finally { setWaterBusy(false); }
  }

  async function createHabit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setSaving(true);
    const form = new FormData(event.currentTarget);
    try {
      onChange(await post<WellnessData>('/api/wellness/habits', {
        name: form.get('name'), frequency: form.get('frequency'), reminderTime: form.get('reminderTime'),
        color: form.get('color'), date: data.date,
      }));
      setHabitOpen(false);
    } catch (error) { onError((error as Error).message); }
    finally { setSaving(false); }
  }

  async function deleteHabit() {
    if (!deletingHabit) return;
    setSaving(true);
    try {
      await remove(`/api/wellness/habits/${deletingHabit.id}`);
      onChange(await get<WellnessData>(`/api/wellness?date=${encodeURIComponent(data.date)}`));
      setDeletingHabit(null);
    } catch (error) { onError((error as Error).message); }
    finally { setSaving(false); }
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setSaving(true);
    const form = new FormData(event.currentTarget);
    try {
      onChange(await api<WellnessData>('/api/wellness/settings', { method: 'PUT', body: JSON.stringify({
        waterGoalMl: form.get('waterGoalMl'), reminderIntervalMinutes: form.get('reminderIntervalMinutes'),
        reminderStartTime: form.get('reminderStartTime'), reminderEndTime: form.get('reminderEndTime'),
        remindersEnabled: form.has('remindersEnabled'), date: data.date,
      }) }));
      setSettingsOpen(false);
    } catch (error) { onError((error as Error).message); }
    finally { setSaving(false); }
  }

  return <>
    <div className="dashboard-wellness-grid">
      <Card className="wellness-habits-card">
        <SectionHeader title="Daily habits" description={`${data.habitSummary.completed} of ${data.habitSummary.total} completed today`}
          action={<Button size="sm" variant="secondary" icon={Plus} onClick={() => setHabitOpen(true)}>New habit</Button>} />
        <Progress value={data.habitSummary.total ? data.habitSummary.completed / data.habitSummary.total * 100 : 0} />
        <div className="habit-checklist">
          {data.habits.map((habit) => <article key={habit.id} className={habit.completed ? 'is-complete' : ''}>
            <button type="button" className="habit-check-action" disabled={busyHabit === habit.id} onClick={() => void checkHabit(habit.id, !habit.completed)}>
              <span className="habit-checkbox" style={{ '--habit-color': habit.color } as CSSProperties}>{habit.completed && <Check size={14} />}</span>
              <span><strong>{habit.name}</strong><small>{habit.reminderTime ? `Reminder ${habit.reminderTime}` : habit.frequency}</small></span>
            </button>
            <em><Flame size={13} />{habit.streak}</em>
            <button type="button" className="habit-delete-button" aria-label={`Discontinue ${habit.name}`} title="Discontinue habit" onClick={() => setDeletingHabit(habit)}><Trash2 size={14} /></button>
          </article>)}
          {!data.habits.length && <div className="wellness-empty"><Sparkles size={18} /><span><strong>Start one small habit</strong><small>Create a recurring habit and check it off here each day.</small></span></div>}
        </div>
      </Card>

      <Card className="hydration-card">
        <SectionHeader title="Water balance" description="Gentle reminders across your workday"
          action={<button type="button" className="wellness-settings-button" aria-label="Hydration settings" onClick={() => setSettingsOpen(true)}><Settings2 size={16} /></button>} />
        <div className="hydration-main">
          <div className="hydration-ring" style={{ '--water-progress': `${data.hydration.progress * 3.6}deg` } as CSSProperties}>
            <span><Droplets size={20} /><strong>{data.hydration.progress}%</strong></span>
          </div>
          <div className="hydration-copy"><span><strong>{(data.hydration.consumedMl / 1000).toFixed(2)} L</strong><small>of {(data.hydration.goalMl / 1000).toFixed(1)} L today</small></span>
            <Progress value={data.hydration.progress} label={`${data.hydration.remainingMl} ml remaining`} />
          </div>
        </div>
        <div className="water-actions"><Button size="sm" variant="secondary" busy={waterBusy} onClick={() => void addWater(250)}>+ 250 ml</Button><Button size="sm" variant="secondary" busy={waterBusy} onClick={() => void addWater(500)}>+ 500 ml</Button></div>
        <div className="water-reminder-note"><BellRing size={14} /><span>{data.settings.remindersEnabled ? `App reminder every ${data.settings.reminderIntervalMinutes} minutes · ${data.settings.reminderStartTime}–${data.settings.reminderEndTime}` : 'Water reminders are paused'}</span></div>
      </Card>
    </div>

    <Modal open={habitOpen} onClose={() => setHabitOpen(false)} title="Create a daily habit" description="Keep it small, specific and easy to check off."
      footer={<><Button variant="secondary" onClick={() => setHabitOpen(false)}>Cancel</Button><Button type="submit" form="habit-form" busy={saving}>Create habit</Button></>}>
      <form id="habit-form" onSubmit={createHabit}><Field label="Habit"><input name="name" required minLength={2} maxLength={80} placeholder="Example: Read for 20 minutes" /></Field>
        <FormGrid><Field label="Repeats"><select name="frequency" defaultValue="daily"><option value="daily">Every day</option><option value="weekdays">Weekdays</option></select></Field><Field label="Reminder time"><WebTimeField name="reminderTime" /></Field></FormGrid>
        <Field label="Color"><input name="color" type="color" defaultValue="#315f75" /></Field>
      </form>
    </Modal>

    <Modal open={Boolean(deletingHabit)} onClose={() => setDeletingHabit(null)} title="Discontinue this habit?"
      description={deletingHabit ? `“${deletingHabit.name}” will be removed from your daily list. Previous check-ins remain safely recorded.` : ''}
      footer={<><Button variant="secondary" onClick={() => setDeletingHabit(null)}>Keep habit</Button><Button variant="danger" busy={saving} onClick={() => void deleteHabit()}>Discontinue</Button></>}>
      <p className="habit-delete-note">You can create the habit again later if you want to restart it.</p>
    </Modal>

    <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} title="Water reminders" description="Set a practical daily target and reminder rhythm."
      footer={<><Button variant="secondary" onClick={() => setSettingsOpen(false)}>Cancel</Button><Button type="submit" form="water-settings-form" busy={saving}>Save reminders</Button></>}>
      <form id="water-settings-form" onSubmit={saveSettings}><FormGrid><Field label="Daily goal (ml)"><input name="waterGoalMl" type="number" min="500" max="10000" step="250" defaultValue={data.settings.waterGoalMl} /></Field><Field label="Remind every"><select name="reminderIntervalMinutes" defaultValue={data.settings.reminderIntervalMinutes}><option value="60">60 minutes</option><option value="90">90 minutes</option><option value="120">2 hours</option><option value="180">3 hours</option></select></Field><Field label="Start reminders"><WebTimeField name="reminderStartTime" defaultValue={data.settings.reminderStartTime} required /></Field><Field label="Stop reminders"><WebTimeField name="reminderEndTime" defaultValue={data.settings.reminderEndTime} required /></Field></FormGrid>
        <label className="check-field"><input name="remindersEnabled" type="checkbox" defaultChecked={data.settings.remindersEnabled} /> Send water reminders to Nosivo and the mobile app</label>
      </form>
    </Modal>
  </>;
}
